/**
 * What the API says about a failed request. Every API error body carries the
 * HTTP status, a stable `code` and the `exceptionId` the server logged it
 * under; validation failures also list the failed `fields`. Older API builds
 * send no code, so callers fall back to the status.
 */
export interface ApiErrorDetails {
  statusCode?: number
  code?: string
  exceptionId?: string
  fields?: string[]
}

function asRecord(value: unknown): Record<string, unknown> | undefined {
  return value !== null && typeof value === 'object'
    ? value as Record<string, unknown>
    : undefined
}

function stringOf(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined
}

/** MSAL owns recovery when the client stops a read before sending it. */
export function isLocalAuthenticationMissing(error: unknown): boolean {
  const detail = asRecord(error)
  return detail?.localAuthMissing === true && detail.code === 'auth_missing'
}

/**
 * The details of a failed request, from an error the API client threw, an
 * error body it returned, or an error wrapping that body in `data`. Pass the
 * response status when the body is all there is.
 */
export function apiErrorDetails(
  error: unknown,
  responseStatus?: number,
): ApiErrorDetails {
  const outer = asRecord(error)
  const body = asRecord(outer?.data) ?? outer
  const status = responseStatus ?? outer?.statusCode ?? body?.statusCode
  const fields = body?.fields
  return {
    statusCode: typeof status === 'number' ? status : undefined,
    code: stringOf(body?.code),
    exceptionId: stringOf(body?.exceptionId),
    fields: Array.isArray(fields)
      ? fields.filter((field): field is string => typeof field === 'string')
      : undefined,
  }
}

const WRITE_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE'])

/** Reads sent as POST, whose failure must not suggest an unsaved change. */
const READ_POST_PATHS = new Set(['/v1/solution-images/batch'])

function isReadPost(request: Request | undefined): boolean {
  if (request?.method !== 'POST')
    return false
  return READ_POST_PATHS.has(new URL(request.url).pathname)
}

/**
 * Whether a write got no readable response although the browser is online.
 * The gateway firewall blocks some text with a 403 that has no CORS headers,
 * which the browser reports as a failed fetch.
 */
export function isUnansweredWrite(
  error: unknown,
  response: Response | undefined,
  request: Request | undefined,
): error is TypeError {
  return error instanceof TypeError
    && !response
    && WRITE_METHODS.has(request?.method ?? '')
    && !isReadPost(request)
    && globalThis.navigator?.onLine !== false
}

/** The translation key for an error code the user can act on. */
const CODE_MESSAGE_KEYS: Partial<Record<string, string>> = {
  opportunity_not_accessible: 'opportunity_not_accessible_in_cep',
  opportunity_change_not_permitted: 'opportunity_change_not_permitted_in_cep',
  opportunity_unavailable: 'opportunity_unavailable_for_completion',
  cep_unavailable: 'cep_temporarily_unavailable',
  cep_completion_failed: 'cep_completion_failed',
  cep_completion_timeout: 'cep_completion_failed',
  cep_completion_not_permitted: 'cep_completion_not_permitted_in_cep',
  project_completion_not_saved: 'project_completion_not_saved',
  project_saved_reload_failed: 'project_saved_reload_failed',
  auth_missing: 'error_session_expired',
  auth_invalid: 'error_session_expired',
  auth_unavailable: 'error_sign_in_unavailable',
  auth_domain_not_allowed: 'error_domain_not_allowed',
  forbidden_role: 'error_permission_denied',
  forbidden: 'error_permission_denied',
  validation_failed: 'error_validation_failed',
  bad_request: 'error_validation_failed',
  not_found: 'error_not_found',
  report_not_found: 'error_not_found',
  web_report_not_created: 'error_not_found',
  conflict: 'error_conflict',
  payload_too_large: 'error_payload_too_large',
  service_unavailable: 'error_service_unavailable',
  bad_gateway: 'error_service_unavailable',
  gateway_timeout: 'error_service_unavailable',
  internal_error: 'something_went_wrong',
  write_unanswered: 'error_write_unanswered',
}

function statusMessageKey(statusCode: number | undefined): string {
  if (statusCode === undefined || statusCode === 0)
    return 'error_network'
  if (statusCode === 401)
    return 'error_session_expired'
  if (statusCode === 403)
    return 'error_permission_denied'
  if (statusCode === 404 || statusCode === 410)
    return 'error_not_found'
  if (statusCode === 409)
    return 'error_conflict'
  if (statusCode === 413)
    return 'error_payload_too_large'
  if (statusCode >= 502 && statusCode <= 504)
    return 'error_service_unavailable'
  if (statusCode >= 400 && statusCode < 500)
    return 'error_validation_failed'
  return 'something_went_wrong'
}

/** The translation key that tells the user what went wrong. */
export function apiErrorMessageKey(details: ApiErrorDetails): string {
  return (details.code && CODE_MESSAGE_KEYS[details.code])
    ?? statusMessageKey(details.statusCode)
}

/**
 * Whether a failed read describes a normal state the caller handles itself
 * (something that does not exist yet, or an admin-only list a regular user
 * may not see) rather than a failure to show.
 */
export function isExpectedReadFailure(details: ApiErrorDetails): boolean {
  return details.statusCode === 404 || details.code === 'forbidden_role'
}

/**
 * The file with characters the gateway firewall rejects in multipart file
 * names (rules 920120/920121) replaced, e.g. the apostrophe in a French
 * "Capture d'écran" screenshot.
 */
export function withUploadSafeName(file: File): File {
  const name = file.name.replace(/['";=]/g, '_')
  return name === file.name
    ? file
    : new File([file], name, { type: file.type, lastModified: file.lastModified })
}
